import _extends from "@babel/runtime/helpers/extends";
import { createModelProvider, externalModelUpdate, externalResetFields, metadataSelector } from "@icg360/rex";
import { Button } from "@icg360/ui-toolkit";
import PT from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import { batch, connect } from "react-redux";
import { getAddressFusionDetails, changeAllPremisesBuildingNumbers, fusionInputsToObject, reduceLocationMeta } from "../utils";
import { memoDeriveForm } from "../utils";
import commercialFusions from ".";
var sharedLocationPropTypes = {
  inputs: PT.array.isRequired,
  meta: PT.shape({
    inputNameSuffix: PT.string.isRequired,
    total: PT.string.isRequired
  }).isRequired
};

var getTheLatestAddressFusionDetails = function getTheLatestAddressFusionDetails(state) {
  var groups = memoDeriveForm.quote(state, {
    commercialFusions: commercialFusions
  });
  return getAddressFusionDetails(groups);
};

var cloneMachine = function cloneMachine(_ref) {
  var _ref$cloneInputs = _ref.cloneInputs,
      cloneInputs = _ref$cloneInputs === void 0 ? [] : _ref$cloneInputs,
      inputNameSuffix = _ref.inputNameSuffix,
      values = _ref.values,
      from = _ref.from,
      to = _ref.to,
      formId = _ref.formId,
      formIndex = _ref.formIndex;

  if (!to || !inputNameSuffix) {
    // eslint-disable-next-line no-console
    console.warn("Missing clone arguments", {
      to: to,
      inputNameSuffix: inputNameSuffix
    });
    return [];
  }

  var toSuffix = inputNameSuffix.replace("#", to);
  var toRegex = new RegExp("^" + toSuffix + ".+|.+" + toSuffix + "$");
  var resetAction = externalResetFields({
    useForm: {
      id: formId,
      index: formIndex
    },
    fields: Object.keys(values).filter(function (name) {
      return toRegex.test(name);
    })
  });

  if (!from) {
    return [resetAction];
  }

  var fromSuffix = inputNameSuffix.replace("#", from);
  var setData = cloneInputs.reduce(function (acc, inputName) {
    var _extends2;

    var hasPrefix = !!values["" + fromSuffix + inputName];
    var toLabel = hasPrefix ? "" + toSuffix + inputName : "" + inputName + toSuffix;
    var fromLabel = hasPrefix ? "" + fromSuffix + inputName : "" + inputName + fromSuffix;
    return _extends({}, acc, (_extends2 = {}, _extends2[toLabel] = values[fromLabel], _extends2));
  }, {});
  return [resetAction, externalModelUpdate({
    formId: formId,
    data: setData
  })];
};

var eraseMachine = function eraseMachine(_ref2) {
  var number = _ref2.number,
      total = _ref2.total,
      inputNameSuffix = _ref2.inputNameSuffix,
      values = _ref2.values,
      formId = _ref2.formId,
      formIndex = _ref2.formIndex;
  var intTotal = parseInt(total, 10);
  var intTo = parseInt(number, 10);
  var intFrom = intTo < intTotal ? intTo + 1 : 0;
  var fromRegex = intFrom ? new RegExp("(^" + inputNameSuffix.replace("#", intFrom) + "(.+)|(.+)" + inputNameSuffix.replace("#", intFrom) + "$)") : undefined;
  var cloneActions = cloneMachine({
    cloneInputs: intFrom ? Object.keys(values).filter(function (name) {
      return fromRegex.test(name);
    }).map(function (key) {
      return key.replace(inputNameSuffix.replace("#", intFrom), "");
    }) : undefined,
    inputNameSuffix: inputNameSuffix,
    values: values,
    from: intFrom,
    to: intTo,
    formId: formId,
    formIndex: formIndex
  });
  return intFrom ? [].concat(cloneActions, eraseMachine({
    number: intFrom,
    total: total,
    inputNameSuffix: inputNameSuffix,
    values: values,
    formId: formId,
    formIndex: formIndex
  })) : cloneActions;
};

export var eraseThunk = function eraseThunk(options) {
  return function (dis, getState) {
    var _createModelProvider = createModelProvider(getState(), {
      type: "JSON",
      onlyTerms: true
    })("default"),
        values = _createModelProvider.values;

    batch(function () {
      eraseMachine(_extends({}, options, {
        values: values
      })).forEach(function (action) {
        dis(action);
      });
      changeAllPremisesBuildingNumbers({
        formId: options.formId,
        addressFusionDetails: getTheLatestAddressFusionDetails(getState()),
        dispatch: dis
      });
    });
  };
}; // Fusion
// name: location
// inputs: locationNumber, additionalLocations, numberOfLocations

LocationFusion.propTypes = _extends({}, sharedLocationPropTypes, {
  erase: PT.func.isRequired
});

function LocationFusion(_ref3) {
  var groupLabel = _ref3.groupLabel,
      erase = _ref3.erase,
      inputs = _ref3.inputs,
      onChange = _ref3.onChange,
      meta = _ref3.meta,
      addressFusionDetails = _ref3.passthroughProps.addressFusionDetails;

  var _fusionInputsToObject = fusionInputsToObject(inputs),
      locationNumber = _fusionInputsToObject.locationNumber,
      additionalLocations = _fusionInputsToObject.additionalLocations,
      numberOfLocations = _fusionInputsToObject.numberOfLocations;

  var locationCount = parseInt(numberOfLocations == null ? void 0 : numberOfLocations.value, 10);
  var intLocations = (additionalLocations == null ? void 0 : additionalLocations.value) === "100" ? locationCount : 1;
  var showDeleteButton = intLocations > 1;
  var fusionDetails = useMemo(function () {
    return addressFusionDetails;
  }, [toString(addressFusionDetails)]);
  var addressDetails = fusionDetails && fusionDetails[groupLabel] ? fusionDetails[groupLabel] : "";
  var locationLabel = addressDetails ? addressDetails != null && addressDetails.address ? ["Location", addressDetails == null ? void 0 : addressDetails.premisesNumber, "Building", addressDetails == null ? void 0 : addressDetails.buildingNumber].join(" ") : "the new location" : "\"Location " + (locationNumber == null ? void 0 : locationNumber.value) + "\""; // We'll wrap these in `setTimout`s below so that we can make the
  // appearance in the UI that something is happening when we delete
  // Rather than just having the UI freeze

  var _useState = useState(false),
      isDeleting = _useState[0],
      setIsDeleting = _useState[1];

  useEffect(function () {
    if (isDeleting) {
      erase(_extends({}, meta, {
        number: locationNumber == null ? void 0 : locationNumber.value,
        total: intLocations
      }));
      onChange({
        additionalLocations: locationCount - 1 === 1 ? "200" : "100",
        numberOfLocations: locationCount - 1
      });
      setIsDeleting(false);
    }
  }, [isDeleting]);
  return /*#__PURE__*/React.createElement("tr", null, showDeleteButton && /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(Button, {
    disabled: isDeleting,
    bsSize: "xsmall",
    onClick: function onClick() {
      if (window.confirm("Are you sure you want to delete " + locationLabel + "?")) {
        setIsDeleting(true);
      }
    }
  }, isDeleting ? "Deleting..." : "Delete")));
}

var mapLocationState = function mapLocationState(state, _ref4) {
  var formId = _ref4.formId,
      formIndex = _ref4.formIndex;
  return {
    meta: reduceLocationMeta(metadataSelector(state, {
      useForm: {
        id: formId,
        index: formIndex
      }
    }).locations)
  };
};

var mapLocationDispatch = function mapLocationDispatch(dispatch, _ref5) {
  var formId = _ref5.formId,
      formIndex = _ref5.formIndex;
  return {
    erase: function erase(options) {
      dispatch(eraseThunk(_extends({
        formId: formId,
        formIndex: formIndex
      }, options)));
    }
  };
};

export var Location = connect(mapLocationState, mapLocationDispatch)(LocationFusion);